import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Database, FileJson, Archive, Check, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Admin-only page that exposes the three backup-download endpoints behind
 * one-click buttons. Each button performs an authenticated `axios.get` with
 * `responseType: "blob"`, then converts the Blob into an object URL and
 * triggers a `<a download>` click — the canonical browser pattern for forcing
 * a file save without opening a tab.
 *
 * Why this page exists: the underlying endpoints require a `Bearer` JWT in
 * the `Authorization` header, which browsers don't send when you paste a URL
 * into the address bar. This UI handles the auth fetch for you.
 */
export default function MigrationExport() {
  const [busy, setBusy] = useState("");

  const downloadAs = async ({ key, url, suggestedName, mime }) => {
    setBusy(key);
    try {
      const r = await axios.get(url, { responseType: "blob" });
      // Pull a server-suggested filename out of the Content-Disposition
      // header when present so the file lands with the timestamped name.
      const cd = r.headers["content-disposition"] || "";
      const fnameMatch = /filename="?([^";]+)"?/.exec(cd);
      const fname = fnameMatch ? fnameMatch[1] : suggestedName;
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${fname}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Download failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <main className="min-h-screen bg-sand-50 px-4 py-10 text-ink-900 sm:px-6">
      <div className="mx-auto max-w-3xl space-y-8">
        <header>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-clay" data-testid="migration-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to Admin Dashboard
          </Link>
          <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Database Migration Export</h1>
          <p className="mt-2 text-sm text-ink-700 max-w-2xl">
            Download a complete snapshot of your production data for migration
            to MongoDB Atlas + Railway. Pick the format that fits your tool.
          </p>
        </header>

        {/* Safety reminder */}
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="migration-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Sensitive data inside.</strong> These exports include
              every user's <code>passwordHash</code> + <code>passwordPlain</code> so
              existing accounts log in normally after migration. Delete the
              downloaded file from your laptop once Atlas restore is verified.
              Re-download anytime — the bundle is regenerated fresh on every click.
            </div>
          </div>
        </div>

        {/* Card 1 — Compass per-collection JSON */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="migration-compass-card">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-emerald-50 p-3 text-emerald-700">
              <FileJson className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">Compass Import Bundle <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">Recommended</span></h2>
              <p className="mt-1 text-sm text-ink-700">
                28 separate <code>.json</code> files — one per collection. Pure JSON arrays in
                MongoDB Extended JSON v2 (relaxed) format. Direct drop-in for
                Compass's <em>Add Data → Import JSON</em> feature. Includes a
                README with step-by-step Atlas instructions.
              </p>
              <Button
                onClick={() => downloadAs({
                  key: "compass",
                  url: `${API}/admin/collections-bundle`,
                  suggestedName: "mdm-sahayak-compass-bundle.tar.gz",
                  mime: "application/gzip",
                })}
                disabled={busy === "compass"}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="migration-download-compass-btn"
              >
                {busy === "compass" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Download Compass Bundle (.tar.gz)
              </Button>
            </div>
          </div>
        </section>

        {/* Card 2 — mongodump native BSON */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="migration-mongodump-card">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-blue-50 p-3 text-blue-700">
              <Archive className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">mongorestore Bundle</h2>
              <p className="mt-1 text-sm text-ink-700">
                Native <code>mongodump</code> BSON archive — preserves all
                indexes. Best for migrations done via the MongoDB CLI
                (<code>mongorestore --uri="…"</code>). Includes a Python
                fallback restore script for users without the Mongo CLI.
              </p>
              <Button
                onClick={() => downloadAs({
                  key: "mongodump",
                  url: `${API}/admin/migration-bundle`,
                  suggestedName: "mdm-sahayak-mongorestore-bundle.tar.gz",
                  mime: "application/gzip",
                })}
                disabled={busy === "mongodump"}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="migration-download-mongodump-btn"
              >
                {busy === "mongodump" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Download mongorestore Bundle (.tar.gz)
              </Button>
            </div>
          </div>
        </section>

        {/* Card 3 — Single JSON archive */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="migration-single-json-card">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-purple-50 p-3 text-purple-700">
              <Database className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">Single JSON Archive</h2>
              <p className="mt-1 text-sm text-ink-700">
                One large JSON file with every collection wrapped under
                <code> archive.collections.&lt;name&gt;</code>. Handy for code-driven
                migrations but <strong>not directly Compass-importable</strong>.
                Pass <code>includePasswords=true</code> to bypass the default
                password redaction.
              </p>
              <Button
                onClick={() => downloadAs({
                  key: "single",
                  url: `${API}/admin/backup?includePasswords=true`,
                  suggestedName: "mdm-sahayak-backup-raw.json",
                  mime: "application/json",
                })}
                disabled={busy === "single"}
                className="mt-4 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="migration-download-single-json-btn"
              >
                {busy === "single" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Download Single JSON (.json)
              </Button>
            </div>
          </div>
        </section>

        {/* iter108 — RazorpayHealthCheck removed along with all payment flows */}

        {/* Verification checklist */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="migration-checklist">
          <h3 className="text-base font-semibold flex items-center gap-2"><Check className="h-5 w-5 text-emerald-600" /> Post-migration checklist</h3>
          <ul className="mt-3 space-y-2 text-sm text-ink-700 list-disc pl-5">
            <li>Restore the bundle into your MongoDB Atlas cluster.</li>
            <li>Set <code>MONGO_URL</code> on Railway to the Atlas connection string.</li>
            <li>Set <code>DB_NAME=test_database</code> (or whatever DB name you restored to).</li>
            <li>Restart the Railway backend service — log in with the same admin/school credentials.</li>
            <li>Verify the admin <code>passwordHash</code> in Compass starts with <code>$2b$12$…</code> — that's how existing logins keep working.</li>
            <li><strong>Change the admin password</strong> via Settings (the current one is in chat history).</li>
            <li><strong>Rotate the Railway <code>JWT_SECRET</code></strong> — invalidates preview tokens.</li>
            <li><strong>Rotate Razorpay LIVE keys</strong> at Razorpay Dashboard.</li>
            <li><strong>Delete the downloaded tarball</strong> from your laptop.</li>
          </ul>
        </section>
      </div>
    </main>
  );
}
